import { Edit2Icon, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import axios from "axios";
import EditModal from "../../components/modal/EditModal";
import DeleteModal from "../../components/modal/DeleteModal";
import useUserRole from "../../app/auth/useUserRole";
import { getToken } from "../../app/auth/authConfig";

const UserManagement = () => {
  const [sampleUsers, setSampleUsers] = useState([]);
  const [tools, setTools] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toolsLoading, setToolsLoading] = useState(true);

  const [userPage, setUserPage] = useState(1);
  const usersPerPage = 10;
  const [userSearch, setUserSearch] = useState("");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const currentUserRole = useUserRole();
  console.log("editData", editData);

  const usersURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/users/`;
  const toolsURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/tools/`;

  useEffect(() => {
    fetchUsers();
    fetchTools();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const token = await getToken();
      const res = await axios.get(usersURL, {
        headers: { "oidc-token": token },
      });
      setSampleUsers(res.data || []);
    } catch (e) {
      console.error("User fetch error", e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTools = async () => {
    try {
      setToolsLoading(true);
      const token = await getToken();
      const res = await axios.get(toolsURL, {
        headers: { "oidc-token": token },
      });
      setTools(res.data || []);
    } catch (e) {
      console.error("Tool fetch error", e);
    } finally {
      setToolsLoading(false);
    }
  };

  const filteredUsers = sampleUsers.filter(
    (user) =>
      user.email.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.role.toLowerCase().includes(userSearch.toLowerCase()) ||
      (user.toolAccess || []).some((g) =>
        g.toLowerCase().includes(userSearch.toLowerCase())
      )
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "-";
    }
  };

  const userTotalPages = Math.ceil(filteredUsers.length / usersPerPage) || 1;

  const paginatedUsers = filteredUsers.slice(
    (userPage - 1) * usersPerPage,
    userPage * usersPerPage
  );

  const toolOptions = tools.map((t) => ({
    label: t.tool_name || t.toolName,
    value: t.tool_name || t.toolName,
  }));

  const handleDelete = async () => {
    if (deleteIndex === null || deleteIndex === undefined) return;

    try {
      setIsDeleting(true);
      const user = filteredUsers[deleteIndex];

      if (!user?.id) {
        setToast({ type: "error", message: "❌ Unable to find selected user" });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      const token = await getToken();
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/users/${user.id}`;

      await axios.delete(baseURL, {
        headers: { "oidc-token": token },
      });

      setToast({ type: "success", message: "✅ User deleted successfully!" });
      setTimeout(() => setToast(null), 3000);

      setShowDeleteModal(false);
      setDeleteIndex(null);
      await fetchUsers();
    } catch (err) {
      console.error("Delete error", err);
      const errorMsg = err?.response?.data?.message || "Failed to delete user";
      setToast({ type: "error", message: `❌ ${errorMsg}` });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsDeleting(false);
    }
  };

  const updateUser = async (formData) => {
    console.log("Updating user with data:", formData);
    try {
      if (formData.toolAccess && !Array.isArray(formData.toolAccess)) {
        formData.toolAccess = [formData.toolAccess];
      }
  
      const token = await getToken();
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/users/update`;
  
      await axios.post(
        baseURL,
        {
          role: formData.role,
          toolAccess: formData.toolAccess,
          userID: editData.id,
        },
        {
          headers: { "oidc-token": token },
        }
      );
  
      await fetchUsers(); // reload table
      setShowEditModal(false);
    } catch (err) {
      console.error("Update failed", err);
      alert("Failed to update user");
    }
  };
  

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
          padding: 32,
          margin: "0 auto",
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <input
            type="text"
            placeholder="Search for Email, Assigned By"
            value={userSearch}
            onChange={(e) => {
              setUserSearch(e.target.value);
              setUserPage(1);
            }}
            disabled={isLoading}
            style={{
              padding: 8,
              width: 300,
              borderRadius: 4,
              border: "1px solid #ccc",
              marginBottom: 8,
              backgroundColor: isLoading ? "#f0f0f0" : "white",
              cursor: isLoading ? "not-allowed" : "auto",
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <span style={{ fontWeight: "bold" }}>
              Total number of Users {filteredUsers.length}
            </span>
            <div style={{ display: "flex", alignItems: "center" }}>
              <button
                onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                disabled={userPage === 1 || isLoading}
                style={{
                  padding: "4px 12px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  background: userPage === 1 || isLoading ? "#eee" : "#fff",
                  cursor: userPage === 1 || isLoading ? "not-allowed" : "pointer",
                }}
              >
                Prev
              </button>
              <span style={{ margin: "0 8px" }}>
                Page {userPage} of {userTotalPages}
              </span>
              <button
                onClick={() =>
                  setUserPage((p) => Math.min(userTotalPages, p + 1))
                }
                disabled={userPage === userTotalPages || isLoading}
                style={{
                  padding: "4px 12px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  background: userPage === userTotalPages || isLoading ? "#eee" : "#fff",
                  cursor:
                    userPage === userTotalPages || isLoading ? "not-allowed" : "pointer",
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: "0 8px",
            fontSize: "14px",
          }}>
            <thead>
              <tr style={{ height: "50px" }}>
                <th style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "left",
                  borderRadius: "8px 0 0 8px",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}>
                  📧 Email
                </th>
                <th style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "left",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}>
                  👤 Role
                </th>
                <th style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "left",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}>
                  � Created By
                </th>
                <th style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "left",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}>
                  📅 Created At
                </th>
                <th style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "center",
                  borderRadius: "0 8px 8px 0",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                  width: "20%",
                }}>
                  ⚙️ Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: usersPerPage }).map((_, idx) => (
                  <tr key={`skeleton-${idx}`} style={{
                    background: "#f8f8f8",
                    borderRadius: "8px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    border: "1px solid #e5e5e5",
                  }}>
                    <td style={{
                      padding: "14px 16px",
                      borderRadius: "8px 0 0 8px",
                      borderBottom: "1px solid #f0f0f0",
                    }}>
                      <div className="h-4 bg-gray-300 rounded w-40 animate-pulse" />
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      borderBottom: "1px solid #f0f0f0",
                    }}>
                      <div className="h-4 bg-gray-300 rounded w-20 animate-pulse" />
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      borderBottom: "1px solid #f0f0f0",
                    }}>
                      <div className="h-4 bg-gray-300 rounded w-32 animate-pulse" />
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      borderBottom: "1px solid #f0f0f0",
                    }}>
                      <div className="h-4 bg-gray-300 rounded w-28 animate-pulse" />
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      borderRadius: "0 8px 8px 0",
                      borderBottom: "1px solid #f0f0f0",
                      textAlign: "center",
                    }}>
                      <div className="flex gap-2 justify-center">
                        <div className="h-8 bg-gray-300 rounded w-20 animate-pulse" />
                        <div className="h-8 bg-gray-300 rounded w-20 animate-pulse" />
                      </div>
                    </td>
                  </tr>
                ))
              ) : paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{
                    padding: "40px 16px",
                    textAlign: "center",
                    background: "#f8f8f8",
                    borderRadius: "8px",
                  }}>
                    <span style={{ color: "#999", fontSize: "15px" }}>
                      📭 No users found
                    </span>
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user, idx) => (
                  <tr key={idx} style={{
                    background: idx % 2 === 0 ? "#bb1ccc0d" : "#f3f0f7",
                    borderRadius: "8px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    border: "1px solid #e5e5e5",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(187,28,204,0.15)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}>
                    <td style={{
                      padding: "14px 16px",
                      borderRadius: "8px 0 0 8px",
                      fontWeight: "500",
                      color: "#333",
                    }}>
                      {user.email}
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      color: "#666",
                    }}>
                      <span style={{
                        background: user.role === "Admin" ? "#ffebee" : "#e3f2fd",
                        color: user.role === "Admin" ? "#c62828" : "#1565c0",
                        padding: "4px 12px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: "600",
                        display: "inline-block",
                      }}>
                        {user.role}
                      </span>
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      color: "#666",
                      fontSize: "13px",
                    }}>
                      {user.createdBy || "-"}
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      color: "#666",
                      fontSize: "13px",
                    }}>
                      {formatDate(user.createdAt)}
                    </td>
                    <td style={{
                      padding: "14px 16px",
                      borderRadius: "0 8px 8px 0",
                      textAlign: "center",
                    }}>
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => {
                            setShowEditModal(true);
                            setEditData(user);
                          }}
                          disabled={currentUserRole !== "Admin"}
                          title={currentUserRole !== "Admin" ? "Only admins can edit users" : "Edit user"}
                          style={{
                            background: currentUserRole === "Admin" ? "linear-gradient(135deg, #bb1ccc 0%, #d946ef 100%)" : "#ccc",
                            color: "white",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            cursor: currentUserRole === "Admin" ? "pointer" : "not-allowed",
                            fontSize: "12px",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            transition: "all 0.3s ease",
                            opacity: currentUserRole === "Admin" ? 1 : 0.6,
                          }}
                          onMouseEnter={(e) => {
                            if (currentUserRole === "Admin") {
                              e.currentTarget.style.transform = "scale(1.05)";
                              e.currentTarget.style.boxShadow = "0 4px 8px rgba(187,28,204,0.3)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        >
                          <Edit2Icon width={14} /> Edit
                        </button>
                        <button
                          onClick={() => {
                            setShowDeleteModal(true);
                            setDeleteIndex((userPage - 1) * usersPerPage + idx);
                          }}
                          disabled={currentUserRole !== "Admin"}
                          title={currentUserRole !== "Admin" ? "Only admins can delete users" : "Delete user"}
                          style={{
                            background: currentUserRole === "Admin" ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" : "#ccc",
                            color: "white",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            cursor: currentUserRole === "Admin" ? "pointer" : "not-allowed",
                            fontSize: "12px",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            transition: "all 0.3s ease",
                            opacity: currentUserRole === "Admin" ? 1 : 0.6,
                          }}
                          onMouseEnter={(e) => {
                            if (currentUserRole === "Admin") {
                              e.currentTarget.style.transform = "scale(1.05)";
                              e.currentTarget.style.boxShadow = "0 4px 8px rgba(239,68,68,0.3)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        >
                          <Trash2 width={14} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showEditModal && editData && (
        <EditModal
          title="Edit User"
          fields={[
            { label: "Email", key: "email", readOnly: true },
            {
              label: "Role",
              key: "role",
              type: "select",
              options: [
                { label: "Admin", value: "Admin" },
                { label: "Member", value: "Member" },
              ],
            },
          ]}
          data={editData}
          onClose={() => setShowEditModal(false)}
          onSave={(formData) => updateUser(formData)}

          
        />
      )}

      {showDeleteModal && (
        <DeleteModal
          title="Are you sure you want to delete this user?"
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          isLoading={isDeleting}
        />
      )}

      {toast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 10000,
            minWidth: 250,
            maxWidth: 420,
            padding: "12px 18px",
            borderRadius: 8,
            color: "#fff",
            fontWeight: 500,
            boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
            background: toast.type === "success" ? "#10b981" : "#ef4444",
            animation: "slideInToast 0.3s ease",
          }}
        >
          {toast.message}
        </div>
      )}

      <style>{`
        @keyframes slideInToast {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default UserManagement;
